export class ObjectUtils {
  static isObject(object: any): boolean {
    return object !== null && typeof object === 'object';
  }

  static isEmpty(object: any): boolean {
    if (!this.isObject(object)) {
      return true;
    }
    return Object.keys(object).length === 0 && object.constructor === Object;
  }

  static isEqual(object1: object, object2: object, deepEqual: boolean = true): boolean {
    return deepEqual ? this.deepEqual(object1, object2) : this.shallowEqual(object1, object2);
  }

  private static deepEqual(object1: object, object2: object): boolean {
    const entries1: [string, any][] = Object.entries(object1);
    const map2: Map<string, any> = new Map(Object.entries(object2));

    if (entries1.length !== map2.size) {
      return false;
    }

    for (const [key, val1] of entries1) {
      const val2: any = map2.get(key);

      const areObjects: boolean = this.isObject(val1) && this.isObject(val2);

      if (areObjects && !this.deepEqual(val1, val2) || !areObjects && val1 !== val2) {
        return false;
      }
    }
    return true;
  }

  private static shallowEqual(object1: object, object2: object): boolean {
    const entries1: [string, any][] = Object.entries(object1);
    const map2: Map<string, any> = new Map(Object.entries(object2));

    if (entries1.length !== map2.size) {
      return false;
    }

    for (const [key, val1] of entries1) {
      if (val1 !== map2.get(key)) {
        return false;
      }
    }
    return true;
  }

  static hasProperty(object: object, propertyName: string, deepCheck: boolean = true): boolean {
    return deepCheck ? this.deepHasProperty(object, propertyName) : this.shallowHasProperty(object, propertyName);
  }

  private static shallowHasProperty(object: object, propertyName: string): boolean {
    return Object.hasOwn(object, propertyName);
  }

  private static deepHasProperty(object: object, propertyName: string): boolean {
    for (const value of Object.values(object)) {
      const isObject: boolean = this.isObject(value);

      if (isObject) {
        if (this.shallowHasProperty(value, propertyName)) {
          return true;
        } else {
          return this.deepHasProperty(value, propertyName);
        }
      }
    }
    return false;
  }

  static deepClone(object: any): any {
    return JSON.parse(JSON.stringify(object));
  }

  /**
   * @example
   * // returns level3 value
   * getNestedValue(obj, 'level1', 'level2', 'level3');
   * @example
   * // returns null if level3 doesn't exists
   * getNestedValue(obj, 'level1', 'level2', 'level3');
   */
  static getNestedValue(object: object, ...args: any): any {
    return args.reduce((obj: any, level: any) => obj?.[level], object) || null;
  }

  static shallowFilterObject(object: object, keyToRemove: string): any {
    return Object.fromEntries(
      Object.entries(object).filter(([key]) => key !== keyToRemove),
    );
  }

  static getObjectbyString(obj: any, propsName: string): any {
    propsName = propsName.replace(/\[(\w+)]/g, '.$1');
    propsName = propsName.replace(/^\./, '');
    const a: string[] = propsName.split('.');
    for (let i: number = 0, n: number = a.length; i < n; ++i) {
      const k: string = a[i];
      if (k in obj) {
        obj = obj[k];
      } else {
        return;
      }
    }
    return obj;
  }

  static isObjEmpty(address: object): boolean {
    return Object.values(address).every(x => x === null || x === '' || x === undefined);
  }
}
